import { useEffect, useState, useSyncExternalStore } from "react";

/**
 * Single source of truth for the live scoreline inside a running timeline.
 *
 * Every SD template bakes `matchInfo` into its own record when it is saved, so
 * slides saved at different moments carry different scorelines - the loop can
 * show 0:1 on one slide and 1:1 on the next - and any slide that has not been
 * re-saved is stale.
 *
 * This store keeps the freshest scoreline seen for the current match, from
 * either source:
 *   - a slide rendering its own matchInfo (reported by the score widget)
 *   - the goal-alarm socket event, which is the earliest the frontend can know
 *
 * Freshness is decided by minute of play, not by arrival order, so an older
 * slide rendering later cannot pull the score backwards. A goal-alarm update
 * always wins, because it is by definition the newest thing we know.
 */

const state = {
  matchId: null,
  result: null,
  minute: -1,        // includes stoppage ("90+5" -> 95); used only for ordering
  displayMinute: -1, // what the clock shows ("90+5" -> 90), matching the old parseInt
  status: null,
  observedAt: 0,
};

// The clock is only advanced while the feed says the ball is in play, and never
// past the end of the half - so it cannot invent stoppage time, and it cannot
// run on through half time or after the whistle.
const TICKING_STATUSES = { firstHalf: 45, secondHalf: 90 };

// If the feed stalls, stop extrapolating rather than drifting indefinitely.
const MAX_DRIFT_MINUTES = 3;

const listeners = new Set();

/** "34." -> 34, "90.+5" / "90.&#x2B;5" -> 95, anything unusable -> -1 */
export const parseMinuteOfPlay = value => {
  if (value == null) return -1;
  const m = String(value)
    .replace(/&#x2B;/g, "+")
    .match(/(\d+)\s*\.?\s*(?:\+\s*(\d+))?/);
  if (!m) return -1;
  return Number(m[1]) + (m[2] ? Number(m[2]) : 0);
};

const emit = () => listeners.forEach(fn => fn());

const commit = (matchId, result, minute, displayMinute, status) => {
  if (!matchId || !result) return;

  // A different fixture resets the store rather than merging into it.
  if (state.matchId && state.matchId !== matchId) {
    state.matchId = matchId;
    state.result = result;
    state.minute = minute;
    state.displayMinute = displayMinute;
    state.status = status;
    state.observedAt = Date.now();
    emit();
    return;
  }

  if (minute < state.minute) return;
  if (state.matchId === matchId && state.result === result && minute === state.minute) return;

  state.matchId = matchId;
  state.result = result;
  state.minute = minute;
  state.displayMinute = displayMinute;
  state.status = status;
  state.observedAt = Date.now();
  emit();
};

/** Called by the score widget with whatever matchInfo its slide was saved with. */
export const reportMatchInfo = matchInfo => {
  commit(
    matchInfo?.MatchId,
    matchInfo?.Result,
    parseMinuteOfPlay(matchInfo?.MinuteOfPlay),
    parseInt(matchInfo?.MinuteOfPlay) || -1,
    matchInfo?.MatchStatus
  );
};

/**
 * Called from the goal-alarm socket handler. The alarm carries the score as
 * "home:away" plus the matchInfo it was built from, and is always treated as
 * the newest value.
 */
export const reportAlarmScore = data => {
  const matchInfo = data?.matchInfo;
  const matchId = matchInfo?.MatchId;
  const result = data?.score || matchInfo?.Result;
  if (!matchId || !result) return;

  const minute = Math.max(
    parseMinuteOfPlay(matchInfo?.MinuteOfPlay),
    state.matchId === matchId ? state.minute : -1
  );

  state.matchId = matchId;
  state.result = result;
  state.minute = minute;
  state.displayMinute = parseInt(matchInfo?.MinuteOfPlay) || state.displayMinute;
  state.status = matchInfo?.MatchStatus || state.status;
  state.observedAt = Date.now();
  emit();
};

const subscribe = fn => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const getSnapshot = () =>
  `${state.matchId}|${state.result}|${state.minute}|${state.displayMinute}|${state.status}`;

/**
 * Freshest known scoreline for this slide's match, or null when the store has
 * nothing newer than what the slide already holds.
 */
export const useLiveScore = matchInfo => {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const matchId = matchInfo?.MatchId;
  if (!matchId || state.matchId !== matchId || !state.result) return null;
  if (state.minute <= parseMinuteOfPlay(matchInfo?.MinuteOfPlay)) return null;

  return { result: state.result, minute: state.minute };
};

/**
 * Freshest known minute for this slide's match, advanced in real time while the
 * ball is in play. Falls back to whatever the slide itself was saved with.
 */
export const useMatchClock = matchInfo => {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [, tick] = useState(0);

  const matchId = matchInfo?.MatchId;
  const known =
    matchId && state.matchId === matchId && state.displayMinute >= 0
      ? state
      : null;

  const ownMinute = parseInt(matchInfo?.MinuteOfPlay) || 0;
  const base = known && known.displayMinute > ownMinute ? known.displayMinute : ownMinute;
  const status = known ? known.status : matchInfo?.MatchStatus;
  const cap = TICKING_STATUSES[status];
  const ticking = cap !== undefined && base < cap;

  // Re-render on a timer only while the clock is actually advancing.
  useEffect(() => {
    if (!ticking) return undefined;
    const id = setInterval(() => tick(n => n + 1), 10000);
    return () => clearInterval(id);
  }, [ticking]);

  if (!ticking) return base;

  const observedAt = known ? known.observedAt : 0;
  if (!observedAt) return base;

  const drift = Math.floor((Date.now() - observedAt) / 60000);
  return Math.min(base + Math.min(drift, MAX_DRIFT_MINUTES), cap);
};

/**
 * Freshest minute of play the feed has reported for this match, stoppage
 * included ("45.+12" -> 57), or -1 when nothing is known yet. Unlike the wall
 * clock since kickoff, this does not run on through half time or stoppage, so
 * "75 minutes" means the 75th minute of the match.
 */
export const useMatchMinute = matchId => {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (!matchId || state.matchId !== matchId) return -1;
  return state.minute;
};

/** Test/reset hook. */
export const __resetLiveScore = () => {
  state.matchId = null;
  state.result = null;
  state.minute = -1;
  state.displayMinute = -1;
  state.status = null;
  state.observedAt = 0;
  emit();
};
